import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BrandHandclapIcon, BrandSearchIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {buildFriendlyOfferListLink} from "../../../offer/helpers/friendly_offer_list/build_friendly_offer_list_link";
import {useGetRegionDetailQuery} from "../../../region/api/get_region_detail";
import {useAppSelector} from "../../../store/hooks";
import {getFullKMLink} from "../../../utils/get_full_km_link";
import {RegistrationThankYouFavoritesModalHeading} from "./RegistrationThankYouFavoritesModalHeading";

interface IProps {
    isOpen: boolean;
    onClose?: () => void;
}

const kmLink = getFullKMLink(kmAppLink.site.offers.favouritesWithParams);

export const RegistrationThankYouFavoritesModal = (props: IProps) => {
    const {isOpen, onClose} = props;
    const preferences = useAppSelector((state) => state.registrationFlow.preferences);

    const isSingleRegion = preferences?.regions.length === 1;
    const singleRegionId = useMemo(() => (isSingleRegion ? preferences?.regions[0] : undefined), [preferences, isSingleRegion]);
    const {data: singleRegionDetail, isLoading: isSingleRegionLoading} = useGetRegionDetailQuery({regionId: singleRegionId!}, {skip: !singleRegionId});

    const listingLink = useMemo(() => {
        if (!preferences) {
            return rpAppLink.offer.multiList();
        }

        const hasRegions = preferences.regions.length > 0;
        const hasMultipleRegions = preferences.regions.length > 1;

        const baseQuery = {
            rooms_0: preferences.rooms.lower,
            rooms_1: preferences.rooms.upper,
            ...(preferences.price ? {price_1: preferences.price} : {})
        };

        if (!hasRegions || hasMultipleRegions) {
            return appendQueryString(rpAppLink.offer.multiList(), {
                ...baseQuery,
                ...(hasRegions ? {region: preferences.regions.map((r) => r.toString())} : {})
            });
        }

        return buildFriendlyOfferListLink({
            ...baseQuery,
            region: singleRegionDetail?.slug || ""
        });
    }, [preferences, singleRegionDetail]);

    const handleClose = () => {
        onClose?.();
    };

    return (
        <Modal isOpen={isOpen} onModalClose={handleClose}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="rounded" className={modalStyle}>
                <div>
                    <RegistrationThankYouFavoritesModalHeading />
                    <div className={cx(sectionBox, sectionMB, mbStyle)}>
                        <BrandHandclapIcon size="4.4" wrapperSize="4.4" wrapperColor={getThemeVariable("colors.primary")} />
                        <div>
                            <Text variant="headline_5" as="p" className={titleStyle}>
                                Sprawdź swoje konto mieszkaniowe
                            </Text>
                            <Text variant="body_copy_2">
                                W Twoim <b>Koncie Mieszkaniowym</b> zobaczysz wszystkie wybrane przez Ciebie oferty oraz nasze rekomendacje dopasowane do
                                przesłanych preferencji.
                            </Text>
                            <Button variant="filled_primary" href={kmLink || ""} target="_blank" size="small" type="button" className={mbStyle}>
                                Zobacz ulubione
                            </Button>
                        </div>
                    </div>
                    <div className={sectionBox}>
                        <BrandSearchIcon size="4.4" wrapperSize="4.4" wrapperColor={getThemeVariable("colors.primary")} />
                        <div>
                            <Text variant="headline_5" as="p" className={titleStyle}>
                                Kontynuuj poszukiwania
                            </Text>
                            <Text variant="body_copy_2">Zobacz wszystkie oferty które spełniają określone przez Ciebie kryteria</Text>
                            <Button
                                variant="outlined_secondary"
                                href={listingLink}
                                target="_self"
                                size="small"
                                type="button"
                                className={mbStyle}
                                disabled={isSingleRegionLoading}
                            >
                                {isSingleRegionLoading ? <Loader size="sm" /> : "kontynuuj poszukiwania"}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const modalStyle = css({
    maxWidth: "840px",
    md: {
        p: 4
    }
});

const sectionBox = css({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexDirection: "row",
    p: 2,
    background: "var(--colors-gray-100)",
    gap: 2
});

const titleStyle = css({
    mb: 1
});

const sectionMB = css({
    mb: 3
});

const mbStyle = css({
    mt: 3
});
